import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import en from '../locales/en.json';
import { LANGUAGE_STORAGE_KEY } from './currency';

/** Any locale catalogue: the JSON module's own shape, treated as opaque data. */
type LocaleBundle = Record<string, unknown>;

/** i18next reads a catalogue from `translation` plus every top-level group. */
function namespaces(bundle: LocaleBundle) {
  return { translation: bundle, ...bundle };
}

function register(lng: string, bundle: LocaleBundle) {
  i18n.addResourceBundle(lng, 'translation', bundle, true, true);
  Object.keys(bundle).forEach((key) => {
    const value = (bundle as Record<string, unknown>)[key];
    if (typeof value === 'object' && value !== null) {
      i18n.addResourceBundle(lng, key, value, true, true);
    }
  });
}

// English is the fallback language and stays in the entry chunk. `el.json` is
// ~479 KB (~103 KB gzipped) - the second-largest asset on a first visit - so it
// is split into its own chunk and fetched only when a Greek reader needs it:
// on boot when the stored preference says Greek, and on the first switch.
let greekBundle: LocaleBundle | null = null;
let greekLoading: Promise<void> | null = null;

function loadGreek(): Promise<void> {
  if (greekBundle) return Promise.resolve();
  if (!greekLoading) {
    greekLoading = import('../locales/el.json')
      .then(
        (mod) =>
          ((mod as unknown as { default?: LocaleBundle }).default ??
            (mod as unknown as LocaleBundle)) as LocaleBundle,
      )
      .then((bundle) => {
        greekBundle = bundle;
        register('el', bundle);
      });
  }
  return greekLoading;
}

i18n.use(LanguageDetector).use(initReactI18next).init({
  resources: {
    en: namespaces(en),
  },
  fallbackLng: 'en',
  interpolation: {
    escapeValue: false, // React already escapes values safely
  },
  detection: {
    order: ['localStorage'],
    caches: ['localStorage'],
    lookupLocalStorage: LANGUAGE_STORAGE_KEY,
  },
});

// A Greek preference is only known after the detector has run, so the bundle is
// registered once i18next is up and the language is re-resolved. The app defers
// its first render to this promise, so Greek readers never see an English frame.
function prefersGreek(): boolean {
  try {
    return (localStorage.getItem(LANGUAGE_STORAGE_KEY) || '').startsWith('el');
  } catch {
    return false;
  }
}

export const i18nReady: Promise<void> = (async () => {
  if (!prefersGreek()) return;
  try {
    await loadGreek();
    await i18n.changeLanguage('el');
  } catch (error) {
    console.error('Greek catalogue failed to load; falling back to English.', error);
  }
})();

// Switching to Greek at runtime waits for the split chunk, so t() never renders
// raw keys while the catalogue is still in flight.
const changeLanguage = i18n.changeLanguage.bind(i18n);
i18n.changeLanguage = (lng, callback) => {
  if (lng && lng.startsWith('el') && !greekBundle) {
    return loadGreek()
      .catch((error) => {
        console.error('Greek catalogue failed to load; falling back to English.', error);
      })
      .then(() => changeLanguage(lng, callback));
  }
  return changeLanguage(lng, callback);
};

// Hot-swap the English catalogue in dev: i18next keeps the object passed to
// init(), so a JSON module update alone would leave the UI showing raw keys
// until a full reload. `el.json` is now a dynamic import, so Vite reloads the
// page on its update, which re-runs this module.
if (import.meta.hot) {
  import.meta.hot.accept(['../locales/en.json'], (modules) => {
    const enModule = modules?.[0] as unknown as { default?: LocaleBundle } | undefined;
    if (enModule) {
      const enData = enModule.default ?? (enModule as unknown as LocaleBundle);
      register('en', enData);
      i18n.emit('languageChanged', i18n.resolvedLanguage || i18n.language || 'en');
    }
  });
}

// Synchronize <html lang="..."> attribute for proper CSS Greek uppercase accent handling
if (typeof document !== 'undefined') {
  const updateHtmlLang = (lng: string) => {
    document.documentElement.lang = lng ? lng.split('-')[0] : 'en';
  };
  updateHtmlLang(i18n.resolvedLanguage || i18n.language || 'en');
  i18n.on('languageChanged', updateHtmlLang);
}

export default i18n;
